import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateProductTypeRequest,
  ProductType,
  UpdateProductTypeRequest,
} from '@nucleus-commerce/api-client/admin';

import { productTypesApi } from '../api/product-types-api';

import { dictionaryKeys } from './dictionary-keys';

/** The detail and the list both carry assignments; the product editor reads either. */
function remember(queryClient: ReturnType<typeof useQueryClient>, row: ProductType) {
  queryClient.setQueryData(dictionaryKeys.productType(row.id), row);
  queryClient.setQueryData<ProductType[]>(dictionaryKeys.productTypes(), (rows) => {
    if (!rows) return rows;
    const index = rows.findIndex((r) => r.id === row.id);
    const next = index === -1 ? [...rows, row] : rows.with(index, row);
    return next.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  });
  return queryClient.invalidateQueries({ queryKey: dictionaryKeys.productTypes(), exact: true });
}

/** 409 ALREADY_EXISTS on `slug`; the editor puts it on the field. */
export function useCreateProductType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductTypeRequest) => productTypesApi.create(body),
    meta: { successMessage: 'productTypes:toasts.created', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

/**
 * The answer carries `affectedVariantCount` (generated SKUs still on the old type code) for the
 * editor's note; the cached row does not keep it.
 */
export function useUpdateProductType(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProductTypeRequest) => productTypesApi.update(id, body),
    meta: { successMessage: 'productTypes:toasts.updated', silentError: true },
    onSuccess: ({ affectedVariantCount: _affected, ...row }) => remember(queryClient, row),
  });
}

/** 409 RESOURCE_IN_USE while a product still has this type; the danger zone shows it. */
export function useDeleteProductType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productTypesApi.remove(id),
    meta: { successMessage: 'productTypes:toasts.deleted', silentError: true },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: dictionaryKeys.productType(id), exact: true });
      queryClient.setQueryData<ProductType[]>(dictionaryKeys.productTypes(), (rows) =>
        rows?.filter((r) => r.id !== id),
      );
      return queryClient.invalidateQueries({
        queryKey: dictionaryKeys.productTypes(),
        exact: true,
      });
    },
  });
}
